import { useState } from "react";
import { toast } from "sonner";
import { MapPin, Phone, Mail, Send, Facebook, Instagram } from "lucide-react";
import { SITE } from "../data/site";
import { SectionEyebrow } from "../components/SectionEyebrow";
import { submitContact } from "../lib/api";

const initialState = { nombre: "", email: "", telefono: "", asunto: "", mensaje: "" };

export default function Contacto() {
    const [form, setForm] = useState(initialState);
    const [loading, setLoading] = useState(false);
    const [submitted, setSubmitted] = useState(false);
    const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!form.nombre || !form.email || !form.asunto || !form.mensaje) {
            toast.error("Completa los campos obligatorios.");
            return;
        }
        setLoading(true);
        try {
            await submitContact(form);
            setSubmitted(true);
            setForm(initialState);
            toast.success("¡Mensaje enviado! Te responderemos pronto.");
        } catch (err) {
            toast.error(err.response?.data?.detail || "Hubo un error. Intenta nuevamente.");
        } finally {
            setLoading(false);
        }
    };

    return (
        <>
            <section className="topo-bg py-20 lg:py-28" data-testid="contact-hero">
                <div className="container-fe max-w-4xl">
                    <SectionEyebrow>Contacto</SectionEyebrow>
                    <h1 className="mt-5 font-display font-bold text-4xl sm:text-5xl lg:text-6xl text-brand-navy leading-[1.05]">
                        Hablemos. Estamos a una <span className="text-brand-green">conversación</span> de distancia.
                    </h1>
                    <p className="mt-7 text-lg text-muted-foreground leading-relaxed">
                        Si quieres donar, ser voluntario, formar una alianza, hacer una consulta o
                        simplemente conocernos, escríbenos. Respondemos rápido y con honestidad.
                    </p>
                </div>
            </section>

            <section className="py-20 lg:py-28 bg-white">
                <div className="container-fe grid lg:grid-cols-5 gap-10">
                    {/* Info */}
                    <div className="lg:col-span-2 space-y-6">
                        <InfoCard icon={<MapPin size={20} />} label="Visítanos" data-testid="contact-info-address">
                            <span className="text-brand-navy font-medium">{SITE.address}</span>
                        </InfoCard>
                        <InfoCard icon={<Phone size={20} />} label="Llámanos" data-testid="contact-info-phone">
                            <a href={`tel:${SITE.phoneRaw}`} className="text-brand-navy font-medium hover:text-brand-green">
                                {SITE.phone}
                            </a>
                        </InfoCard>
                        <InfoCard icon={<Mail size={20} />} label="Escríbenos" data-testid="contact-info-email">
                            <a href={`mailto:${SITE.email}`} className="text-brand-navy font-medium hover:text-brand-green break-all">
                                {SITE.email}
                            </a>
                        </InfoCard>

                        <div className="rounded-2xl border border-black/5 bg-brand-cream p-6">
                            <div className="text-xs uppercase tracking-wider font-bold text-brand-green">Síguenos</div>
                            <div className="mt-3 flex gap-2">
                                <a
                                    href={SITE.facebook}
                                    target="_blank"
                                    rel="noreferrer"
                                    data-testid="contact-facebook"
                                    aria-label="Facebook"
                                    className="h-11 w-11 rounded-full bg-white text-brand-navy flex items-center justify-center hover:bg-brand-green hover:text-white transition-colors"
                                >
                                    <Facebook size={18} />
                                </a>
                                <a
                                    href={SITE.instagram}
                                    target="_blank"
                                    rel="noreferrer"
                                    data-testid="contact-instagram"
                                    aria-label="Instagram"
                                    className="h-11 w-11 rounded-full bg-white text-brand-navy flex items-center justify-center hover:bg-brand-green hover:text-white transition-colors"
                                >
                                    <Instagram size={18} />
                                </a>
                            </div>
                        </div>

                        {/* Map */}
                        <div className="rounded-2xl overflow-hidden border border-black/5 aspect-square" data-testid="contact-map">
                            <iframe
                                title="Ubicación Fundación Formar Estructura"
                                src="https://www.openstreetmap.org/export/embed.html?bbox=-75.2400%2C4.4350%2C-75.1900%2C4.4570&layer=mapnik&marker=4.4480%2C-75.2200"
                                className="w-full h-full"
                                loading="lazy"
                            />
                        </div>
                    </div>

                    {/* Form */}
                    <div className="lg:col-span-3">
                        <div className="rounded-3xl bg-brand-cream border border-black/5 p-8 lg:p-10">
                            {submitted ? (
                                <div data-testid="contact-success" className="text-center py-12">
                                    <div className="h-16 w-16 rounded-full bg-brand-green text-white flex items-center justify-center mx-auto">
                                        <Send size={26} />
                                    </div>
                                    <h3 className="mt-5 font-display font-bold text-2xl text-brand-navy">¡Mensaje recibido!</h3>
                                    <p className="mt-3 text-muted-foreground">Pronto te responderemos al correo registrado.</p>
                                    <button onClick={() => setSubmitted(false)} className="btn-ghost-pill mt-6" data-testid="contact-new-message">
                                        Enviar otro mensaje
                                    </button>
                                </div>
                            ) : (
                                <form onSubmit={handleSubmit} className="space-y-5" data-testid="contact-form">
                                    <h2 className="font-display font-bold text-2xl text-brand-navy">Envíanos un mensaje</h2>
                                    <div className="grid sm:grid-cols-2 gap-5">
                                        <Field label="Nombre completo *">
                                            <input
                                                data-testid="contact-input-nombre"
                                                value={form.nombre}
                                                onChange={(e) => set("nombre", e.target.value)}
                                                className={inputCls}
                                                required
                                            />
                                        </Field>
                                        <Field label="Correo electrónico *">
                                            <input
                                                type="email"
                                                data-testid="contact-input-email"
                                                value={form.email}
                                                onChange={(e) => set("email", e.target.value)}
                                                className={inputCls}
                                                required
                                            />
                                        </Field>
                                    </div>
                                    <Field label="Teléfono">
                                        <input
                                            data-testid="contact-input-telefono"
                                            value={form.telefono}
                                            onChange={(e) => set("telefono", e.target.value)}
                                            className={inputCls}
                                        />
                                    </Field>
                                    <Field label="Asunto *">
                                        <input
                                            data-testid="contact-input-asunto"
                                            value={form.asunto}
                                            onChange={(e) => set("asunto", e.target.value)}
                                            className={inputCls}
                                            placeholder="¿Sobre qué quieres hablarnos?"
                                            required
                                        />
                                    </Field>
                                    <Field label="Mensaje *">
                                        <textarea
                                            data-testid="contact-input-mensaje"
                                            value={form.mensaje}
                                            onChange={(e) => set("mensaje", e.target.value)}
                                            className={`${inputCls} min-h-[160px]`}
                                            rows={6}
                                            required
                                        />
                                    </Field>
                                    <button type="submit" disabled={loading} className="btn-primary-pill w-full sm:w-auto disabled:opacity-50" data-testid="contact-submit-button">
                                        {loading ? "Enviando..." : (<><Send size={16} /> Enviar mensaje</>)}
                                    </button>
                                </form>
                            )}
                        </div>
                    </div>
                </div>
            </section>
        </>
    );
}

const InfoCard = ({ icon, label, children, ...rest }) => (
    <div className="rounded-2xl border border-black/5 bg-white p-6 hover:border-brand-green/30 transition-colors" {...rest}>
        <div className="flex items-start gap-4">
            <div className="h-11 w-11 rounded-xl bg-brand-green-light text-brand-green flex items-center justify-center shrink-0">
                {icon}
            </div>
            <div>
                <div className="text-xs uppercase tracking-wider font-bold text-brand-green">{label}</div>
                <div className="mt-1.5 text-sm leading-relaxed">{children}</div>
            </div>
        </div>
    </div>
);

const inputCls =
    "w-full h-12 rounded-xl border border-border bg-white px-4 text-brand-navy placeholder:text-muted-foreground/60 focus:outline-none focus:ring-2 focus:ring-brand-green focus:border-transparent transition-all";

const Field = ({ label, children }) => (
    <div>
        <label className="block text-sm font-semibold text-brand-navy mb-2">{label}</label>
        {children}
    </div>
);
